<template>
  <div class="mchat-page">
    <!-- 顶部导航栏 -->
    <div class="mchat-header">
      <div class="header-left">
        <template v-if="currentView === 'chat'">
          <button class="header-btn" @click="goBackToList">
            <i class="el-icon-arrow-left"></i>
          </button>
          <span class="header-title">{{ activeConversation ? (activeConversation.customerName || activeConversation.customerId) : '聊天' }}</span>
        </template>
        <template v-else>
          <span class="header-title">对话列表</span>
        </template>
      </div>
      <div class="header-right">
        <template v-if="currentView === 'list'">
          <button class="header-btn" @click="toggleGlobalSound">
            <i class="el-icon-service" :style="{ color: globalSoundEnabled ? '#409eff' : '#c0c4cc' }"></i>
          </button>
          <el-dropdown trigger="click" @command="handleStatusChange" class="status-dropdown">
            <button class="header-btn status-btn">
              <span class="status-dot" :class="staffStatus"></span>
              <span class="status-label">{{ statusLabels[staffStatus] }}</span>
              <i class="el-icon-arrow-down"></i>
            </button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="online">
                <span class="status-dot online"></span> 上线
              </el-dropdown-item>
              <el-dropdown-item command="busy">
                <span class="status-dot busy"></span> 忙碌
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
        <template v-else>
          <button class="header-btn" @click="showChatActions = !showChatActions">
            <i class="el-icon-more"></i>
          </button>
          <div v-if="showChatActions" class="chat-actions-popup" @click="showChatActions = false">
            <div class="chat-actions-menu" @click.stop>
              <div class="action-item" @click="handleViewBlacklist">
                <i class="el-icon-document-copy"></i>
                <span>查看黑名单</span>
              </div>
              <div class="action-item" @click="handleLogout">
                <i class="el-icon-switch-button"></i>
                <span>退出登录</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 搜索栏（列表页） -->
    <div v-if="currentView === 'list'" class="mchat-search-bar">
      <div class="search-type-tabs">
        <span :class="['search-type-tab', { active: searchType === 'message' }]" @click="searchType = 'message'; clearGlobalSearch()">聊天记录</span>
        <span :class="['search-type-tab', { active: searchType === 'user' }]" @click="searchType = 'user'; clearGlobalSearch()">用户信息</span>
      </div>
      <div class="search-input-wrapper">
        <i class="el-icon-search search-icon"></i>
        <input v-model="globalSearchKeyword" :placeholder="searchType === 'message' ? '搜索聊天记录...' : '搜索用户信息...'" class="search-input" @keyup.enter="performGlobalSearch" />
        <button v-if="globalSearchKeyword" class="search-clear" @click="clearGlobalSearch"><i class="el-icon-close"></i></button>
      </div>
    </div>

    <!-- 搜索结果 -->
    <div v-if="currentView === 'list' && showGlobalSearchResults" class="mchat-search-results">
      <div class="search-results-header">
        <span>搜索结果: {{ globalSearchResults.length }} 条</span>
        <button class="close-btn" @click="clearGlobalSearch">关闭</button>
      </div>
      <div v-if="globalSearching" class="search-loading"><i class="el-icon-loading"></i> 搜索中...</div>
      <div v-else-if="globalSearchResults.length === 0" class="no-search-results">没有找到相关记录</div>
      <div v-else class="search-results-list">
        <div v-for="(result, index) in globalSearchResults" :key="index" class="search-result-item" @click="jumpToSearchResult(result)">
          <template v-if="searchType === 'message'">
            <div class="result-header">
              <span class="result-name">{{ result.customerName || result.customerId }}</span>
              <span class="result-time">{{ formatTime(result.createTime) }}</span>
            </div>
            <div class="result-content" v-html="highlightKeyword(result.content, globalSearchKeyword)"></div>
            <div class="result-staff">客服: {{ result.receiverName || '未知' }}</div>
          </template>
          <template v-if="searchType === 'user'">
            <div class="result-header">
              <span class="result-name">{{ result.customerName || result.customerId }}</span>
              <span class="result-user-id">ID: {{ result.customerId }}</span>
            </div>
            <div class="result-content">
              <span v-if="result.customerLocation">📍 {{ result.customerLocation }}</span>
              <span v-if="result.customerIp">🌐 {{ result.customerIp }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 对话列表视图 -->
    <div v-show="currentView === 'list'" class="mchat-list-view">
      <div v-if="loading" class="loading-indicator"><i class="el-icon-loading"></i> 加载中...</div>
      <div v-else-if="adminList.length === 0" class="empty-state">
        <div class="empty-icon">💬</div>
        <div class="empty-text">暂无对话</div>
      </div>
      <div v-else class="admin-list">
        <div v-for="admin in adminList" :key="admin.staffId" class="admin-group">
          <div class="admin-header" @click="toggleAdminExpand(admin.staffId)">
            <div class="admin-info">
              <span class="status-dot" :class="admin.isOnline ? 'online' : 'offline'"></span>
              <span class="admin-name">{{ admin.staffName }}</span>
              <span class="customer-count">{{ admin.sessions ? admin.sessions.length : 0 }}个客户</span>
            </div>
            <div class="admin-badges">
              <span v-if="getAdminHasNewMessages(admin)" class="new-badge">✦ NEW ✦</span>
              <span v-if="admin.staffUnread > 0" class="unread-count">{{ admin.staffUnread }}</span>
              <i :class="['el-icon-arrow-down', 'expand-arrow', { expanded: expandedAdmins.includes(admin.staffId) }]"></i>
            </div>
          </div>
          <div v-if="expandedAdmins.includes(admin.staffId)" class="customer-list">
            <div v-for="friend in admin.sessions" :key="friend.id" :class="['customer-item', { active: getCustomerConversationId(friend, admin.staffId) === activeConversationId }]" @click="selectCustomerConversation(friend, admin)">
              <div class="customer-avatar">
                <img :src="defaultAvatar" :alt="friend.customerName" />
              </div>
              <div class="customer-info">
                <div class="customer-name-row">
                  <span class="customer-name">{{ friend.customerName || friend.customerId }}</span>
                  <span class="customer-badge">{{ friend.customerIdentity == 'USER' ? '客户' : '游客' }}</span>
                  <span :class="['online-tag', friend.onlineStats ? 'online' : 'offline']">{{ friend.onlineStats ? '在线' : '离线' }}</span>
                </div>
                <div class="customer-last-msg">{{ friend.lastMsgContent || '暂无消息' }}</div>
                <div class="customer-time">{{ formatTime(friend.lastMsgTime) }}</div>
              </div>
              <div v-if="getCustomerUnreadCount(friend) > 0" class="customer-unread">{{ getCustomerUnreadCount(friend) }}</div>
              <button class="close-customer-btn" @click.stop="hideCustomerSession(friend, admin)"><i class="el-icon-close"></i></button>
            </div>
            <div v-if="!admin.sessions || admin.sessions.length === 0" class="no-customers">暂无客户</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 聊天视图 -->
    <div v-show="currentView === 'chat'" class="mchat-chat-view">
      <div class="messages-container" ref="messagesContainer">
        <div v-if="!activeConversation" class="no-conversation">请选择一个对话</div>
        <div v-else>
          <div v-if="loadingMoreMessages" class="loading-more"><i class="el-icon-loading"></i> 加载更多...</div>
          <MessageItem v-for="message in messages" :key="message.id" :message="message" :conversation="activeConversation" @recall-message="handleRecallMessage" @scrollToBottom="scrollToBottom" />
        </div>
      </div>
      <div v-if="activeConversation" class="message-input-area">
        <div class="input-tools">
          <el-upload ref="imageUpload" action="" :auto-upload="false" :show-file-list="false" accept="image/*" :on-change="handleImageSelect">
            <button class="tool-btn"><i class="el-icon-picture"></i></button>
          </el-upload>
          <button v-if="!activeConversation.isBlacklisted" class="tool-btn" @click="handleBlacklistUser"><i class="el-icon-delete"></i></button>
          <button v-if="activeConversation.isBlacklisted" class="tool-btn" @click="handleUnblacklistUser"><i class="el-icon-success"></i></button>
        </div>
        <div class="input-row">
          <textarea v-model="messageText" class="message-textarea" placeholder="输入消息..." :disabled="sending" @keydown="handleKeyDown" rows="1"></textarea>
          <button class="send-btn" :disabled="!messageText.trim() || sending" @click="sendMessage"><i class="el-icon-s-promotion"></i></button>
        </div>
      </div>
    </div>

    <!-- 黑名单对话框 -->
    <div v-if="blacklistDialogVisible" class="modal-overlay" @click="blacklistDialogVisible = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <span>黑名单列表</span>
          <button class="modal-close" @click="blacklistDialogVisible = false"><i class="el-icon-close"></i></button>
        </div>
        <div class="modal-body">
          <div v-if="blacklistLoading" class="loading-indicator"><i class="el-icon-loading"></i> 加载中...</div>
          <div v-else-if="blacklistData.length === 0" class="empty-state">暂无黑名单记录</div>
          <div v-else class="blacklist-items">
            <div v-for="item in blacklistData" :key="item.userId" class="blacklist-item">
              <div class="blacklist-info">
                <div class="blacklist-name">{{ item.userName || item.userId }}</div>
                <div class="blacklist-detail">ID: {{ item.userId }} | IP: {{ item.ipAddress }}</div>
                <div class="blacklist-reason">原因: {{ item.reason }}</div>
              </div>
              <button class="unblacklist-btn" @click="removeFromBlacklist(item.userId)">解除</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MessageItem from "@/components/MessageItem.vue";
import wsService from "@/services/websocket.js";
import { formatBeijingTime } from '@/utils/time'
import { decryptAES, isBase64String } from '@/utils/crypto'
import defaultAvatar from "@/assets/images/defaultavatar.png"

export default {
  name: "MChat",
  components: {
    MessageItem,
  },
  data() {
    return {
      defaultAvatar,
      currentView: 'list',
      showChatActions: false,
      loading: false,
      adminList: [],
      expandedAdmins: [],
      globalSoundEnabled: true,
      staffStatus: "online",
      statusLabels: { online: "上线", busy: "忙碌" },
      statusChanging: false,
      searchType: "message",
      globalSearchKeyword: "",
      globalSearching: false,
      showGlobalSearchResults: false,
      globalSearchResults: [],
      wsConnected: false,
      refreshInterval: null,
      isRefreshing: false,
      autoRefreshInterval: 10000,
      hasInitialized: false,
      messageText: "",
      sending: false,
      loadingMessages: false,
      loadingMoreMessages: false,
      hasMoreMessages: true,
      currentPage: 1,
      pageSize: 1000,
      blacklistDialogVisible: false,
      blacklistLoading: false,
      blacklistData: [],
      blacklistPageNum: 1,
      blacklistPageSize: 20,
      blacklistTotal: 0,
    };
  },
  computed: {
    activeConversation() {
      return this.$store.getters.activeConversation;
    },
    activeConversationId() {
      return this.$store.state.activeConversationId;
    },
    messages() {
      const conversationId = this.$store.state.activeConversationId;
      return this.$store.getters.conversationMessages(conversationId);
    },
  },
  watch: {
    activeConversation: {
      handler(newConv, oldConv) {
        if (newConv && (!oldConv || newConv.customId !== oldConv.customId)) {
          this.loadMessages();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const savedSoundEnabled = localStorage.getItem('globalSoundEnabled');
    if (savedSoundEnabled !== null) {
      this.globalSoundEnabled = savedSoundEnabled === 'true';
    }
    const savedStaffStatus = localStorage.getItem('staffStatus');
    if (savedStaffStatus && (savedStaffStatus === 'online' || savedStaffStatus === 'busy')) {
      this.staffStatus = savedStaffStatus;
    }
    if (window.wsService) {
      window.wsService.disconnect();
    }
    window.wsService = wsService;
    this.loadConversations();
    this.startAutoRefresh();
    // 初始化时根据状态连接WebSocket
    if (this.staffStatus === 'online') {
      this.connectWebSocket();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (wsService) {
      wsService.removeMessageHandler(this.handleWebSocketMessage);
      wsService.removeSystemHandler(this.handleWebSocketSystemEvent);
      wsService.disconnect();
    }
  },
  methods: {
    goBackToList() {
      this.currentView = 'list';
      this.showChatActions = false;
    },
    goToChat() {
      this.currentView = 'chat';
      this.showChatActions = false;
    },
    async loadConversations() {
      this.loading = true;
      try {
        await this.$store.dispatch("loadConversations");
        this.adminList = this.$store.state.conversations || [];
        console.log(this.adminList,'adminList');
        
        if (this.adminList.length > 0 && this.expandedAdmins.length === 0) {
          this.expandedAdmins.push(this.adminList[0].staffId);
        }
      } catch (error) {
        console.error("Failed to load conversations:", error);
      } finally {
        this.loading = false;
      }
    },
    toggleAdminExpand(staffId) {
      const index = this.expandedAdmins.indexOf(staffId);
      if (index > -1) {
        this.expandedAdmins.splice(index, 1);
      } else {
        this.expandedAdmins.push(staffId);
      }
    },
    getAdminHasNewMessages(admin) {
      if (!admin) return false;
      if (admin.staffUnread && admin.staffUnread > 0) return true;
      if (admin.sessions) {
        for (const s of admin.sessions) {
          if (s.staffUnread && s.staffUnread > 0) return true;
        }
      }
      return false;
    },
    getCustomerConversationId(friend, staffId) {
      return "conv_" + staffId + "_" + friend.customerId;
    },
    getCustomerUnreadCount(friend) {
      return friend.staffUnread || 0;
    },
    selectCustomerConversation(friend, admin) {
      localStorage.setItem("selected_session_id", friend.id);
      const conversation = {
        ...friend,
        customId: "conv_" + admin.staffId + "_" + friend.customerId,
      };
      this.$store.commit("SET_ACTIVE_CONVERSATION", conversation.customId);
      this.$emit("conversation-selected", conversation);
      if (!this.wsConnected) {
        this.connectWebSocket(conversation);
      }
      this.goToChat();
    },
    async hideCustomerSession(friend, admin) {
      try {
        const response = await this.$http.post(`/api/session/hide`, {
          sessionId: `${friend.id}`,
        });
        if (response.data && response.data.code === 200) {
          this.$store.commit("REMOVE_CUSTOMER_SESSION", {
            adminId: admin.staffId,
            customerId: friend.customerId,
          });
          const customerConversationId = this.getCustomerConversationId(friend, admin.staffId);
          if (this.activeConversationId === customerConversationId) {
            this.$store.commit("SET_ACTIVE_CONVERSATION", null);
          }
          await this.loadConversations();
        }
      } catch (error) {
        console.error("Failed to hide session:", error);
      }
    },
    async handleStatusChange(command) {
      if (this.statusChanging || this.staffStatus === command) return;
      this.statusChanging = true;
      try {
        this.staffStatus = command;
        localStorage.setItem('staffStatus', command);
        this.$emit("status-changed", command);
        if (command === 'online') {
          this.$message.success("已切换为上线状态");
          // 上线状态：连接WebSocket并添加处理器
          this.wsConnected = false;
          this.connectWebSocket();
        } else {
          this.$message.warning("已切换为忙碌状态");
          // 忙碌状态：断开WebSocket连接并清理处理器
          if (wsService) {
            wsService.removeMessageHandler(this.handleWebSocketMessage);
            wsService.removeSystemHandler(this.handleWebSocketSystemEvent);
            this.wsConnected = false;
            wsService.disconnect();
          }
        }
      } finally {
        this.statusChanging = false;
      }
    },
    toggleGlobalSound() {
      this.globalSoundEnabled = !this.globalSoundEnabled;
      localStorage.setItem('globalSoundEnabled', this.globalSoundEnabled.toString());
      this.$message.success(this.globalSoundEnabled ? "声音已开启" : "声音已关闭");
    },
    async performGlobalSearch() {
      const keyword = this.globalSearchKeyword.trim();
      if (!keyword) return;
      this.globalSearching = true;
      this.showGlobalSearchResults = true;
      this.globalSearchResults = [];
      try {
        const apiUrl = this.searchType === 'user' ? '/api/session/search' : '/api/message/search';
        const response = await this.$http.get(apiUrl, {
          params: { keyword, pageNum: 1, pageSize: 50 }
        });
        if (response.data && response.data.code === 200) {
          const data = isBase64String(response.data.data) ? decryptAES(response.data.data) : response.data.data;
          const records = data.records || [];
          this.globalSearchResults = records.map(record => ({
            ...record,
            displayName: record.customerName || record.customerId,
          }));
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        this.globalSearching = false;
      }
    },
    clearGlobalSearch() {
      this.globalSearchKeyword = "";
      this.showGlobalSearchResults = false;
      this.globalSearchResults = [];
    },
    highlightKeyword(content, keyword) {
      if (!content || !keyword) return content;
      const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedKeyword})`, 'gi');
      return content.replace(regex, '<span class="highlight">$1</span>');
    },
    async jumpToSearchResult(result) {
      let foundSession = null;
      let foundAdmin = null;
      for (const admin of this.adminList) {
        if (admin.sessions) {
          const session = admin.sessions.find(s => s.customerId == result.customerId);
          if (session) { foundSession = session; foundAdmin = admin; break; }
        }
      }
      if (foundSession && foundAdmin) {
        this.selectCustomerConversation(foundSession, foundAdmin);
        this.clearGlobalSearch();
      }
    },
    async connectWebSocket(conversation) {
      if (this.wsConnected && wsService.messageHandlers && wsService.messageHandlers.length > 0) return;
      try {
        const loginId = (conversation && conversation.senderAccount) || localStorage.getItem('login_id');
        if (!loginId) return;
        if (!this.wsConnected) {
          await wsService.connect(loginId);
          this.wsConnected = true;
        }
        wsService.addMessageHandler(this.handleWebSocketMessage);
        wsService.addSystemHandler(this.handleWebSocketSystemEvent);
      } catch (error) {
        console.error("WebSocket connect failed:", error);
        this.wsConnected = false;
      }
    },
    handleWebSocketMessage(message) {
      if (message.event === "MESSAGE" && message.data) {
        const msgData = message.data;
        const loginId = localStorage.getItem("login_id");
        if (this.activeConversation && msgData.sessionId === this.activeConversation.id) {
          const newMessage = {
            id: msgData.messageId,
            content: msgData.content,
            type: msgData.messageType,
            sender: msgData.senderId,
            receiver: msgData.receiverId,
            createTime: formatBeijingTime(),
            isOwn: msgData.senderId == loginId,
          };
          this.$store.commit("ADD_MESSAGE", {
            conversationId: this.activeConversation.customId,
            message: newMessage,
          });
          this.$nextTick(() => this.scrollToBottom());
        }
        if (msgData.senderId != loginId && this.globalSoundEnabled) {
          this.playNotificationSound();
        }
      }
    },
    handleWebSocketSystemEvent(event, data) {
      if (event === "recall" && data && data.messageId && this.activeConversation) {
        this.$store.commit("RECALL_MESSAGE", {
          conversationId: this.activeConversation.customId,
          messageId: data.messageId,
        });
      }
      // 收到会话更新事件（包括在线状态变化），刷新对话列表
      if (event === "session_update" || event === "connect" || event === "disconnect") {
        this.refreshConversations();
      }
    },
    playNotificationSound() {
      try {
        let audioContext = window.audioContext;
        if (!audioContext) {
          audioContext = new (window.AudioContext || window.webkitAudioContext)();
          window.audioContext = audioContext;
        }
        const play = () => {
          const now = audioContext.currentTime;
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 800;
          oscillator.type = 'sine';
          const beepDuration = 0.1;
          const beepInterval = 0.2;
          gainNode.gain.setValueAtTime(0, now);
          for (let i = 0; i < 3; i++) {
            const startTime = now + i * beepInterval;
            gainNode.gain.setValueAtTime(0.3, startTime);
            gainNode.gain.setValueAtTime(0, startTime + beepDuration);
          }
          oscillator.start(now);
          oscillator.stop(now + 2 * beepInterval + beepDuration);
        };
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(play).catch(play);
        } else {
          play();
        }
      } catch (e) {
        console.error("Play sound failed:", e);
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshInterval = setInterval(() => {
        this.refreshConversations();
      }, this.autoRefreshInterval);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async refreshConversations() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      try {
        await this.$store.dispatch("loadConversations");
        this.adminList = this.$store.state.conversations || [];
      } catch (error) {
        console.error("Refresh failed:", error);
      } finally {
        this.isRefreshing = false;
      }
    },
    async loadMessages() {
      this.currentPage = 1;
      this.hasMoreMessages = true;
      if (this.activeConversation) {
        await this.$store.dispatch("loadMessages", {
          conversationId: this.$store.state.activeConversationId,
          conversation: this.activeConversation,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
        });
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    async loadMoreMessages() {
      if (this.loadingMoreMessages || !this.hasMoreMessages || !this.activeConversation) return;
      this.loadingMoreMessages = true;
      const nextPage = this.currentPage + 1;
      try {
        const container = this.$refs.messagesContainer;
        const oldScrollHeight = container ? container.scrollHeight : 0;
        await this.$store.dispatch("loadMessages", {
          conversationId: this.$store.state.activeConversationId,
          conversation: this.activeConversation,
          pageNum: nextPage,
          pageSize: this.pageSize,
          prepend: true,
        });
        const messages = this.messages;
        if (messages.length < nextPage * this.pageSize) {
          this.hasMoreMessages = false;
        } else {
          this.currentPage = nextPage;
        }
        this.$nextTick(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - oldScrollHeight;
          }
        });
      } catch (error) {
        console.error("Load more messages failed:", error);
      } finally {
        this.loadingMoreMessages = false;
      }
    },
    async sendMessage() {
      if (!this.messageText.trim() || !this.activeConversation || this.sending) return;
      if (this.staffStatus === 'busy') {
        this.$message.warning('当前处于忙碌状态，无法发送消息');
        return;
      }
      if (!window.wsService || !window.wsService.isConnected) {
        this.$message.error('未连接到服务器');
        return;
      }
      const text = this.messageText.trim();
      const messageId = Date.now().toString();
      this.messageText = "";
      this.sending = true;
      try {
        const message = {
          id: messageId,
          content: text,
          type: 1,
          sender: this.activeConversation.senderAccount,
          receiver: this.activeConversation.customerId,
          createTime: formatBeijingTime(),
          isOwn: true,
          status: "sending",
        };
        this.$store.commit("ADD_MESSAGE", {
          conversationId: this.$store.state.activeConversationId,
          message: message,
        });
        const success = window.wsService.sendMessage(
          text,
          this.activeConversation.customerId,
          this.activeConversation.senderAccount,
          messageId
        );
        if (success) {
          message.status = "sent";
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else {
          message.status = "failed";
          this.$message.error("发送失败");
          this.messageText = text;
        }
      } catch (error) {
        console.error("Send message failed:", error);
        this.$message.error("发送失败");
        this.messageText = text;
      } finally {
        this.sending = false;
      }
    },
    async handleImageSelect(file) {
      try {
        const formData = new FormData();
        formData.append("file", file.raw);
        const response = await this.$http.post("/api/message/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data && response.data.code === 200) {
          const imageUrl = isBase64String(response.data.data) ? decryptAES(response.data.data) : response.data.data || "";
          if (!window.wsService || !window.wsService.isConnected) return;
          const messageId = Date.now().toString();
          const message = {
            id: messageId,
            content: imageUrl,
            type: 2,
            sender: this.activeConversation.senderAccount,
            receiver: this.activeConversation.customerId,
            createTime: formatBeijingTime(),
            isOwn: true,
            status: "sending",
          };
          this.$store.commit("ADD_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            message: message,
          });
          const success = window.wsService.sendImageMessage(
            imageUrl,
            this.activeConversation.customerId,
            this.activeConversation.senderAccount,
            messageId
          );
          if (success) {
            message.status = "sent";
            this.scrollToBottom();
          } else {
            message.status = "failed";
            this.$message.error("图片发送失败");
          }
        }
      } catch (error) {
        console.error("Upload image failed:", error);
        this.$message.error("图片上传失败");
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    handleRecallMessage(messageId) {
      this.$confirm("确定要撤回这条消息吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        this.recallMessage(messageId);
      }).catch(() => {});
    },
    async recallMessage(messageId) {
      try {
        const response = await this.$http.post("/api/message/recall", { messageId });
        if (response.data && response.data.code === 200) {
          this.$store.commit("RECALL_MESSAGE", {
            conversationId: this.$store.state.activeConversationId,
            messageId: messageId,
          });
          this.$message.success("消息已撤回");
        }
      } catch (error) {
        console.error("Recall failed:", error);
        this.$message.error("撤回失败");
      }
    },
    handleKeyDown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    async handleViewBlacklist() {
      this.blacklistDialogVisible = true;
      this.blacklistPageNum = 1;
      await this.loadBlacklistData();
    },
    async loadBlacklistData() {
      this.blacklistLoading = true;
      try {
        const response = await this.$http.get("/api/blacklist", {
          params: { pageNum: this.blacklistPageNum, pageSize: this.blacklistPageSize },
        });
        if (response.data && response.data.code === 200) {
          let data = response.data.data;
          if (isBase64String(data)) {
            const decrypted = decryptAES(data);
            data = typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
          }
          this.blacklistData = data.records || [];
          this.blacklistTotal = parseInt(data.total) || 0;
        }
      } catch (error) {
        console.error("Load blacklist failed:", error);
      } finally {
        this.blacklistLoading = false;
      }
    },
    removeFromBlacklist(id) {
      this.$confirm("确定要解除拉黑吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(async () => {
        try {
          const response = await this.$http.put(`/api/blacklist/${id}`);
          if (response.data && response.data.code === 200) {
            this.$message.success("已解除拉黑");
            await this.loadBlacklistData();
            await this.$store.dispatch("loadConversations");
          }
        } catch (error) {
          this.$message.error("解除拉黑失败");
        }
      }).catch(() => {});
    },
    handleBlacklistUser() {
      if (!this.activeConversation) return;
      this.$confirm(
        `确定要拉黑用户 "${this.activeConversation.customerName}" 吗？`,
        "提示",
        { type: "warning" }
      ).then(async () => {
        try {
          const response = await this.$http.post("/api/blacklist", {
            userId: this.activeConversation.customerId,
            userName: this.activeConversation.customerName,
            ipAddress: this.activeConversation.customerIp || "",
            reason: "客服拉黑",
          });
          if (response.data && response.data.code === 200) {
            this.$message.success("用户已拉黑");
            if (this.activeConversation) {
              this.activeConversation.isBlacklisted = true;
            }
            await this.$store.dispatch("loadConversations");
          }
        } catch (error) {
          this.$message.error("拉黑失败");
        }
      }).catch(() => {});
    },
    handleUnblacklistUser() {
      if (!this.activeConversation) return;
      this.$confirm(
        `确定要解除拉黑用户 "${this.activeConversation.customerName}" 吗？`,
        "提示",
        { type: "warning" }
      ).then(async () => {
        try {
          const response = await this.$http.put(`/api/blacklist/${this.activeConversation.customerId}`);
          if (response.data && response.data.code === 200) {
            this.$message.success("已解除拉黑");
            if (this.activeConversation) {
              this.activeConversation.isBlacklisted = false;
            }
            await this.$store.dispatch("loadConversations");
          }
        } catch (error) {
          this.$message.error("解除拉黑失败");
        }
      }).catch(() => {});
    },
    handleLogout() {
      this.$confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        this.$store.dispatch("logout");
        this.$router.push("/login");
        if (window.wsService) {
          window.wsService.disconnect();
        }
      }).catch(() => {});
    },
    formatTime(timestamp) {
      if (!timestamp || isNaN(Number(timestamp))) return "";
      const ts = Number(timestamp);
      const finalTs = ts.toString().length === 10 ? ts * 1000 : ts;
      const date = new Date(finalTs);
      if (date.toString() === "Invalid Date") return "";
      const now = new Date();
      const dateYmd = date.toLocaleDateString("zh-CN");
      const nowYmd = now.toLocaleDateString("zh-CN");
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayYmd = yesterday.toLocaleDateString("zh-CN");
      if (dateYmd === nowYmd) {
        return date.toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
      if (dateYmd === yesterdayYmd) {
        return "昨天";
      }
      return date
        .toLocaleDateString("zh-CN", {
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-");
    },
  },
};
</script>

<style scoped>
.mchat-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  overflow: hidden;
  overflow-x: hidden;
  overflow-y: hidden;
  font-size: 14px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}
.mchat-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  flex-shrink: 0;
  z-index: 100;
  position: relative;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.header-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.header-btn:active {
  background: rgba(255, 255, 255, 0.2);
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status-btn {
  font-size: 13px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}
.status-label {
  font-size: 12px;
}
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
.status-dot.online {
  background-color: #67c23a;
  box-shadow: 0 0 4px rgba(103, 194, 58, 0.6);
}
.status-dot.offline {
  background-color: #f56c6c;
}
.status-dot.busy {
  background-color: #e6a23c;
  box-shadow: 0 0 4px rgba(230, 162, 60, 0.6);
}
.chat-actions-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.3);
}
.chat-actions-menu {
  position: absolute;
  top: 52px;
  right: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 140px;
}
.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.action-item:last-child {
  border-bottom: none;
}
.action-item:active {
  background: #f5f5f5;
}
.action-item i {
  font-size: 18px;
  color: #666;
}
.mchat-search-bar {
  padding: 10px 12px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}
.search-type-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 8px;
  background: #f0f2f5;
  border-radius: 6px;
  overflow: hidden;
}
.search-type-tab {
  flex: 1;
  text-align: center;
  padding: 6px 0;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}
.search-type-tab.active {
  background: #667eea;
  color: white;
  font-weight: 500;
}
.search-input-wrapper {
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 0 10px;
}
.search-icon {
  color: #999;
  font-size: 16px;
  margin-right: 6px;
}
.search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 0;
  font-size: 14px;
  outline: none;
}
.search-clear {
  background: none;
  border: none;
  color: #999;
  padding: 4px;
  cursor: pointer;
}
.mchat-search-results {
  background: white;
  border-bottom: 1px solid #e8e8e8;
  max-height: 250px;
  overflow-y: auto;
  flex-shrink: 0;
}
.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  font-size: 12px;
  color: #666;
}
.close-btn {
  background: none;
  border: none;
  color: #409eff;
  font-size: 12px;
  cursor: pointer;
}
.search-loading, .no-search-results {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.search-result-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.search-result-item:active {
  background: #f5f7fa;
}
.result-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}
.result-name {
  font-weight: 500;
  font-size: 13px;
  color: #333;
}
.result-time, .result-user-id {
  font-size: 11px;
  color: #999;
}
.result-content {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}
.result-content :deep(.highlight) {
  background: #ffd54f;
  color: #333;
  padding: 0 2px;
  border-radius: 2px;
}
.result-staff {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}
.mchat-list-view {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.loading-indicator {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
.empty-state {
  padding: 60px 20px;
  text-align: center;
}
.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}
.empty-text {
  color: #999;
  font-size: 14px;
}
.admin-group {
  margin-bottom: 0;
}
.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.admin-header:active {
  background: #f5f7fa;
}
.admin-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.admin-name {
  font-weight: 600;
  font-size: 15px;
  color: #333;
}
.customer-count {
  font-size: 11px;
  color: #999;
  background: #f0f2f5;
  padding: 2px 6px;
  border-radius: 4px;
}
.admin-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.new-badge {
  font-size: 9px;
  color: #f56c6c;
  background: #fff5f5;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #f56c6c;
  animation: blink 1.2s ease-in-out infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.unread-count {
  background: #f56c6c;
  color: white;
  font-size: 11px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  padding: 0 5px;
  font-weight: 500;
}
.expand-arrow {
  font-size: 14px;
  color: #999;
  transition: transform 0.2s;
}
.expand-arrow.expanded {
  transform: rotate(180deg);
}
.customer-list {
  background: #fafafa;
}
.customer-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  position: relative;
}
.customer-item.active {
  background: #e8f0fe;
  border-left: 3px solid #667eea;
}
.customer-item:active {
  background: #e8f0fe;
}
.customer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  flex-shrink: 0;
}
.customer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.customer-info {
  flex: 1;
  min-width: 0;
}
.customer-name-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}
.customer-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.customer-badge {
  font-size: 9px;
  background: #67c23a;
  color: white;
  padding: 1px 4px;
  border-radius: 2px;
  flex-shrink: 0;
}
.online-tag {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 2px;
  flex-shrink: 0;
}
.online-tag.online {
  color: #67c23a;
  background: #f0f9eb;
}
.online-tag.offline {
  color: #999;
  background: #f5f5f5;
}
.customer-last-msg {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}
.customer-time {
  font-size: 11px;
  color: #ccc;
}
.customer-unread {
  position: absolute;
  top: 12px;
  right: 40px;
  background: #f56c6c;
  color: white;
  font-size: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0 4px;
}
.close-customer-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(245, 108, 108, 0.1);
  border: none;
  color: #f56c6c;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}
.customer-item:hover .close-customer-btn {
  opacity: 1;
}
.no-customers {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.mchat-chat-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  -webkit-overflow-scrolling: touch;
}
.no-conversation {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
.loading-more {
  text-align: center;
  padding: 12px;
  color: #999;
  font-size: 12px;
}
.message-input-area {
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
}
.input-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.tool-btn {
  background: none;
  border: 1px solid #e8e8e8;
  color: #666;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}
.tool-btn:active {
  background: #f5f5f5;
}
.input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.message-textarea {
  flex: 1;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  resize: none;
  line-height: 1.5;
  max-height: 100px;
  font-family: inherit;
  background: #fafbfc;
}
.message-textarea:focus {
  border-color: #667eea;
  background: white;
}
.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}
.send-btn:disabled {
  background: #e8e8e8;
  color: #ccc;
  box-shadow: none;
}
.send-btn:not(:disabled):active {
  transform: scale(0.95);
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.modal-content {
  background: white;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 16px;
}
.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 4px;
}
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}
.blacklist-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}
.blacklist-info {
  flex: 1;
  min-width: 0;
}
.blacklist-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
}
.blacklist-detail {
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
}
.blacklist-reason {
  font-size: 12px;
  color: #999;
}
.unblacklist-btn {
  background: #409eff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
}
.unblacklist-btn:active {
  opacity: 0.8;
}
</style>
